/**
 * ContentGiftsContent
 *
 * Body of the content "gifts received" sheet — the content owner's paginated
 * view of who has gifted / unlocked a piece of content. Mirrors Flutter's
 * content_gifts_bottom_sheet.dart body (lines 287-352):
 *
 *   - appBarTitle() (287-308) → the coins header row: the content's total
 *     received coins, compact-formatted via `MathUtils.getReactionsIntlNumber`
 *     (NOT the per-tile `roundDoubleTwoDecimals`), next to the exclusive-tinted
 *     coins icon.
 *   - gridView()    (310-335) → a 3-column `VirtualList` grid of
 *     `GiftGiverGridItem` tiles with on-scroll load-more (`gridThreeColumnsCount`).
 *   - emptyListWidget() (337-352) → the "No gifts yet" empty state, shown when
 *     settled with no gifts.
 *
 * Data + pagination come from `useContentGifts` (Task 4). Tapping a tile
 * navigates to that gifter's user-details (content_gifts_bottom_sheet.dart:82-85).
 */

import React, { useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { Navigator } from '@/presentation/routing/Navigator';
import { MathUtils } from '@/domain/utils/mathUtils';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { useGridColumnsCount } from '@/presentation/hooks';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { useContentGifts } from './hooks/useContentGifts';
import { GiftGiverGridItem } from './components/GiftGiverGridItem';

export interface ContentGiftsContentProps {
  mediaPath: string;
  coins: number;
}

/**
 * Stable key for a gift tile. The wire has no document id, so mirror the
 * de-dupe key used by `useContentGifts` (gifter + creation time).
 */
function giftItemKey(gift: AppTransactionEntity): string {
  return `${gift.usersInfo.userId}_${gift.createdDate}`;
}

export const ContentGiftsContent: React.FC<ContentGiftsContentProps> = ({
  mediaPath,
  coins,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const gridColumnsCount = useGridColumnsCount();

  const { gifts, isLoading, hasMore, loadMore } = useContentGifts(mediaPath);

  const handleTileTap = useCallback((userId: string) => {
    Navigator.navigateToUserDetails(userId);
  }, []);

  const renderGiftItem = useCallback(
    (gift: AppTransactionEntity) => (
      <GiftGiverGridItem transaction={gift} onTap={handleTileTap} />
    ),
    [handleTileTap],
  );

  const handleReachedEnd = useCallback(() => {
    if (!isLoading && hasMore) void loadMore();
  }, [isLoading, hasMore, loadMore]);

  // Coarse lifecycle token reusing existing state: spinner with no tiles yet is
  // `loading`; settled with no tiles is `empty`; otherwise `ready`.
  const sheetStatus =
    isLoading && gifts.length === 0
      ? 'loading'
      : gifts.length === 0
        ? 'empty'
        : 'ready';

  return (
    <div
      {...qaAttr({ id: 'content-gifts-sheet', status: sheetStatus })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header: compact total received coins + coins icon (Flutter appBarTitle). */}
      <div
        {...qaAttr({ id: 'content-gifts-header' })}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: responsive.gap.xs,
          padding: responsive.spacing.base,
        }}
      >
        <span
          style={{
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.bold,
            color: theme.text.primary,
          }}
        >
          {MathUtils.getReactionsIntlNumber(coins)}
        </span>
        <AssetIcon
          path="/assets/images/coins.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.exclusive}
        />
      </div>

      {/* Body: empty state, initial spinner, or the paginated 3-column grid. */}
      {isLoading && gifts.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: responsive.spacing.xl,
          }}
        >
          <ExpauseLoadingIndicator />
        </div>
      ) : gifts.length === 0 ? (
        <div
          {...qaAttr({ id: 'content-gifts-empty' })}
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            paddingTop: responsive.spacing['3xl'],
            textAlign: 'center',
            color: theme.text.primary,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.bold,
          }}
        >
          {l10n.gift.noGiftsYet}
        </div>
      ) : (
        <div
          className="scrollbar-hide"
          {...qaAttr({ id: 'content-gifts-list' })}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: responsive.spacing.base,
          }}
        >
          <VirtualList<AppTransactionEntity>
            items={gifts}
            renderItem={renderGiftItem}
            getItemKey={giftItemKey}
            gridProps={{
              // `useGridColumnsCount` resolves the count centrally: inside this
              // sheet surface it returns the fixed 3 columns matching Flutter's
              // gridThreeColumnsCount (content_gifts_bottom_sheet.dart gridView).
              // The tile keeps the 3/4 avatar aspect ratio
              // (gridThreeColumnsCircleBorderAvatarAspectRatio).
              columnsCount: gridColumnsCount,
              crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
              mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
              itemAspectRatio: CONTENT_CONSTANTS.giftGiverAspectRatioValue,
              bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
            }}
            onReachedEnd={handleReachedEnd}
            backgroundColor={theme.modalBackground}
          />

          {isLoading && gifts.length > 0 && (
            <div
              style={{
                padding: responsive.spacing.base,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ExpauseLoadingIndicator />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
